import React, { useMemo } from 'react';
import { Button, Empty } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import type { Park } from '../../types';
import {
  isNationalPark,
  isPendingPark,
  isProvincialPark,
} from '../../utils/helpers';
import { ParkFileAttachments } from './ParkFileAttachments';
import { hasParkProfile } from '../../constants/parkProfiles';

/** 详情面板 Props */
export interface DetailPanelProps {
  selectedParkId: string | null | undefined;
  parks: Park[];
  onOpenProfile?: (parkId: string) => void;
}

function DetailRow(props: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="dash-detail-row">
      <div className="dash-detail-row__label">{props.label}</div>
      <div className="dash-detail-row__value">{props.children}</div>
    </div>
  );
}

function TagList(props: { items: string[] }): React.ReactElement {
  if (props.items.length === 0) return <>—</>;
  return (
    <div className="dash-tag-list">
      {props.items.map((t) => (
        <span key={t} className="dash-tag">
          {t}
        </span>
      ))}
    </div>
  );
}

/**
 * 右侧园区详情（悬浮式半透明面板）
 */
export function DetailPanel(props: DetailPanelProps): React.ReactElement {
  const { selectedParkId, parks } = props;

  const selected = useMemo(() => {
    if (!selectedParkId) return undefined;
    return parks.find((p) => p.id === selectedParkId);
  }, [parks, selectedParkId]);

  if (!selectedParkId || !selected) {
    return (
      <aside className="dash-panel dash-panel--right">
        <div className="dash-detail-empty">
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<span className="dash-empty-text">请选择园区</span>}
          />
        </div>
      </aside>
    );
  }

  if (isNationalPark(selected)) {
    return (
      <aside className="dash-panel dash-panel--right">
        <div className="dash-detail-title">园区详情 · 国家级</div>
        <div className="dash-detail-body">
          <DetailRow label="园区名称">{selected.name}</DetailRow>
          <DetailRow label="省份">{selected.province || '—'}</DetailRow>
          <DetailRow label="市">{selected.city || '—'}</DetailRow>
          <DetailRow label="区">{selected.district?.trim() ? selected.district : '—'}</DetailRow>
          <DetailRow label="范围">{selected.range || '—'}</DetailRow>
          <DetailRow label="建设周期">{selected.buildingCycle || '—'}</DetailRow>
          <DetailRow label="对接情况">{selected.dockingStatus || '—'}</DetailRow>
          <DetailRow label="投资额">
            {selected.investmentBracket?.trim()
              ? selected.investmentBracket
              : '—'}
          </DetailRow>
          <DetailRow label="可建设落地项目类型">
            <TagList items={selected.projectTypes} />
          </DetailRow>
          {selected.technicalAdvantages.length > 0 ? (
            <DetailRow label="技术优势">
              <TagList items={selected.technicalAdvantages} />
            </DetailRow>
          ) : null}
          <DetailRow label="基本情况">
            {selected.basicSituation?.trim() ? (
              <span className="dash-detail-text-block">{selected.basicSituation}</span>
            ) : (
              '—'
            )}
          </DetailRow>
          <ParkFileAttachments park={selected} alwaysShowFileRow />
        </div>
      </aside>
    );
  }

  if (isPendingPark(selected)) {
    return (
      <aside className="dash-panel dash-panel--right">
        <div className="dash-detail-title">园区详情 · 待开发</div>
        <div className="dash-detail-body">
          {hasParkProfile(selected.name) && props.onOpenProfile ? (
            <Button
              type="primary"
              block
              className="dash-profile-entry"
              onClick={() => props.onOpenProfile?.(selected.id)}
            >
              进入园区专题详情 <ArrowRightOutlined />
            </Button>
          ) : null}
          <DetailRow label="园区名称">{selected.name}</DetailRow>
          <DetailRow label="省份">{selected.province || '—'}</DetailRow>
          <DetailRow label="市">{selected.city || '—'}</DetailRow>
          <DetailRow label="区">{selected.district || '—'}</DetailRow>
          <ParkFileAttachments park={selected} />
          <DetailRow label="对接情况">{selected.dockingStatus || '—'}</DetailRow>
          <DetailRow label="综合能源消费量">
            {selected.comprehensiveEnergyConsumption?.trim() || '—'}
          </DetailRow>
          <DetailRow label="园区地区生产总值">
            {selected.grossRegionalProduct != null
              ? `${selected.grossRegionalProduct.toLocaleString('zh-CN')} 亿元${selected.grossRegionalProductYear ? `（${selected.grossRegionalProductYear}年）` : ''}`
              : selected.grossRegionalProductStatement || '未公布官方数据'}
          </DetailRow>
          <DetailRow label="统计口径">
            {selected.grossRegionalProductScope || '—'}
          </DetailRow>
          <DetailRow label="数据核验">
            {selected.grossRegionalProductVerificationStatus === 'verified'
              ? '官方数据，已核验'
              : selected.grossRegionalProductVerificationStatus === 'official_estimate'
                ? '官方预期值（不参与筛选与排序）'
                : selected.grossRegionalProductVerificationStatus === 'official_qualitative'
                  ? '官方定性表述（不参与筛选与排序）'
                  : '未公布官方数据'}
          </DetailRow>
          {selected.grossRegionalProductSourceUrl ? (
            <DetailRow label="数据来源">
              <a
                href={selected.grossRegionalProductSourceUrl}
                target="_blank"
                rel="noreferrer"
              >
                {selected.grossRegionalProductSource || '查看官方来源'}
              </a>
            </DetailRow>
          ) : null}
          <DetailRow label="是否有绿电资源">
            {selected.hasGreenElectricity ? '是' : '否'}
          </DetailRow>
          <DetailRow label="绿电资源规模">
            {selected.greenElectricityScale?.trim() || '—'}
          </DetailRow>
          <DetailRow label="主导产业类型">
            <TagList items={selected.leadingIndustries} />
          </DetailRow>
          <DetailRow label="园区基本情况">
            {selected.basicSituation?.trim() ? (
              <span className="dash-detail-text-block">{selected.basicSituation}</span>
            ) : (
              '—'
            )}
          </DetailRow>
          {selected.failReason?.trim() ? (
            <DetailRow label="申报失败原因">
              <span className="dash-detail-text-block">{selected.failReason}</span>
            </DetailRow>
          ) : null}
          {selected.remark?.trim() ? (
            <DetailRow label="备注">
              <span className="dash-detail-text-block">{selected.remark}</span>
            </DetailRow>
          ) : null}
        </div>
      </aside>
    );
  }

  if (isProvincialPark(selected)) {
    return (
      <aside className="dash-panel dash-panel--right">
        <div className="dash-detail-title">园区详情 · 省级</div>
        <div className="dash-detail-body">
          <DetailRow label="园区名称">{selected.name}</DetailRow>
          <DetailRow label="省份">{selected.province || '—'}</DetailRow>
          <DetailRow label="市">{selected.city || '—'}</DetailRow>
          <DetailRow label="区">{selected.district || '—'}</DetailRow>
          <ParkFileAttachments park={selected} />
          <DetailRow label="对接情况">{selected.dockingStatus || '—'}</DetailRow>
          <DetailRow label="综合能源消费量">
            {selected.comprehensiveEnergyConsumption?.trim() || '—'}
          </DetailRow>
          <DetailRow label="是否有绿电资源">
            {selected.hasGreenElectricity ? '是' : '否'}
          </DetailRow>
          <DetailRow label="绿电资源规模">
            {selected.greenElectricityScale?.trim() || '—'}
          </DetailRow>
          <DetailRow label="主导产业类型">
            <TagList items={selected.leadingIndustries} />
          </DetailRow>
          <DetailRow label="申报失败原因">
            {selected.failReason?.trim() ? (
              <span className="dash-detail-text-block">{selected.failReason}</span>
            ) : (
              '—'
            )}
          </DetailRow>
          <DetailRow label="备注">
            {selected.remark?.trim() ? (
              <span className="dash-detail-text-block">{selected.remark}</span>
            ) : (
              '—'
            )}
          </DetailRow>
        </div>
      </aside>
    );
  }

  return (
    <aside className="dash-panel dash-panel--right">
      <div className="dash-detail-empty">
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span className="dash-empty-text">请选择园区</span>}
        />
      </div>
    </aside>
  );
}
